import dayjs from 'dayjs';
import _ from 'lodash';

import {
  Eフェーズ種別,
  E入力分類,
  E有無フラグ,
  E科目別計画設定番号,
  E補助科目フラグ,
  E計画表示モード,
} from '@/openapi/enum';
import { E0円非表示フラグ, データListItemResponse } from '@/openapi/user';

import { MONTHS_OF_YEAR } from '@/classes/common/Constants';
import 決算期Utility, { 決算期TableHeader } from '@/classes/common/決算期Utility';
import AbstractPlbscfService, {
  PlbscfListItem,
  PlbscfTypeList,
  PlbscfYearItem,
  PlbscfYearValue,
  YearItemType,
  貸借CalcConfig,
} from '@/classes/service/AbstractPlbscfService';
import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import Abstract計画PlbscfService, {
  Bs,
  Pl,
  Plbscf業績予測ListItem,
  帳票Type,
  業績予測,
  補助科目別計画ListItem,
  計画Plbscf,
} from '@/classes/service/Abstract計画PlbscfService';
import 分析PlbscfService from '@/classes/service/分析/分析PlbscfService';
import 償却Service from '@/classes/service/分析計画/償却Service';
import 返済明細Service from '@/classes/service/計画/返済明細Service';
import { PlbscfType, ValueType } from '@/master/Plbscf';

// ------------------------------------------------------------------
// types
// ------------------------------------------------------------------

type 計画表示モードHeaders = Record<keyof typeof E計画表示モード, 決算期TableHeader[]>;

export default class 計画PlbscfService extends Abstract計画PlbscfService<計画Plbscf> {
  protected serviceType = ServiceType.計画Plbscf;

  /** 計算用補助科目別計画データ */
  private calc補助科目別計画List: 補助科目別計画ListItem[] = [];

  /** 貸借・損益用ヘッダー */
  private plbsHeaders: 計画表示モードHeaders = {
    月次計画第0期: [],
    月次計画第1期: [],
    年次計画: [],
  };

  /** 科目別・補助科目別用ヘッダー */
  private 科目別Headers: 計画表示モードHeaders = {
    月次計画第0期: [],
    月次計画第1期: [],
    年次計画: [],
  };

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------

  /**
   * @override
   * @param eフェーズ種別
   */
  public constructor(eフェーズ種別: Eフェーズ種別 | null = null) {
    super(eフェーズ種別);

    this.all決算期 = 決算期Utility.getAll決算期();

    // ヘッダーを準備しておく
    this.onlyYear決算期 = {
      header: this.all決算期.header.filter((h) => h.month === undefined),
      years: this.all決算期.years,
    };
    this.plbsHeaders = {
      月次計画第0期: this.create月次計画Header(true, true),
      月次計画第1期: this.create月次計画Header(false, true),
      年次計画: this.onlyYear決算期.header,
    };
    this.科目別Headers = {
      月次計画第0期: this.create月次計画Header(true),
      月次計画第1期: this.create月次計画Header(),
      年次計画: this.onlyYear決算期.header,
    };
  }

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse(
    データs: データListItemResponse[],
    option?: Record<string, string | number | AbstractデータService<unknown>>
  ): 計画Plbscf {
    // 帳票かつ既に計算済の場合は再計算しない
    if (this.is帳票 && 計画PlbscfService.計画Plbscf !== null) return 計画PlbscfService.計画Plbscf;

    // 各service初期化・parse
    if (this.分析PlbscfService === null) {
      this.分析PlbscfService =
        option?.分析PlbscfService === undefined
          ? new 分析PlbscfService(Eフェーズ種別.分析フェーズ)
          : (option?.分析PlbscfService as 分析PlbscfService);
    }
    this.分析PlbscfService.set勘定科目s = this.勘定科目s;

    if (this.償却Service === null) {
      this.償却Service =
        option?.償却Service === undefined
          ? new 償却Service(Eフェーズ種別.計画フェーズ)
          : (option?.償却Service as 償却Service);
    }
    this.償却Service.set勘定科目s = this.勘定科目s;
    this.償却Service.set部門s = this.部門s;

    if (this.返済明細Service === null) {
      this.返済明細Service =
        option?.返済明細Service === undefined
          ? new 返済明細Service(Eフェーズ種別.計画フェーズ)
          : (option?.返済明細Service as 返済明細Service);
    }
    this.返済明細Service.set勘定科目s = this.勘定科目s;
    this.返済明細Service.set部門s = this.部門s;

    this.分析PlbscfTypeLists = this.分析PlbscfService
      .parse(データs, { isNoFilter: true })
      .sort((a, b) => a.type - b.type);
    this.償却 = this.償却Service.parse(データs, { functionType: '償却予測額' });
    this.返済明細Service.parse(データs);
    this.oldLoans = this.返済明細Service.createTデータOldLoans(データs);

    // 計画のtデータを設定（PLBSCF、科目別計画）
    const plbscfデータs = this.フェーズ種別Filter(this.入力分類Filter(データs, [E入力分類.PLBSCF]));
    this.rawTデータPlbscfs = this.createTデータPlbscfs(plbscfデータs);
    this.tデータ科目別計画Configs = this.createTデータ科目別計画Configs(データs, E科目別計画設定番号.計画フェーズ);
    this.tデータYearConfigs = this.createTデータ科目別計画Configs(データs, E科目別計画設定番号.計画フェーズ, true);

    // 画面表示用データを生成
    const v計画Plbscf = this.calcAndCreate計画Plbscf();

    // 帳票で再計算させないために詰めておく
    計画PlbscfService.計画Plbscf = v計画Plbscf;
    return v計画Plbscf;
  }

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, string | number>
  ): 帳票データ[] {
    const v計画Plbscf = this.parse(データs, option);

    if (option === undefined) throw new Error('no option');
    const plbscfType = option.plbscfType as PlbscfType;
    const v帳票Type = option.帳票Type as 帳票Type;

    let list: 帳票データ[] = [];
    switch (v帳票Type) {
      case 帳票Type.補助科目別:
        // 補助科目別（シート数 = 1）
        list = this.create補助科目別帳票データs();
        break;

      case 帳票Type.科目別:
        // 科目別（シート数 = 部門数）
        list = this.create科目別帳票データs(
          v計画Plbscf.科目別損益計算書,
          '科目別計画',
          計画PlbscfService.計画書.c計画表示モード === E計画表示モード.年次計画
        );
        break;

      case 帳票Type.部門別:
        // 部門別の損益（シート数 = 部門数）
        list = v計画Plbscf.pls
          .filter((pl) => pl.部門pk !== undefined) // 合計以外
          .map((pl) => {
            const v部門 = this.部門s.find((b) => b.pk === pl.部門pk)!;
            return {
              sheetName: this.createXlsxSheetName(`部門別計画-${v部門.c部門名}`),
              ...this.createPlbscf帳票データ(plbscfType, pl, true),
            };
          });
        break;

      default:
        // 通常のplbscf（シート数 = 1）
        switch (plbscfType) {
          case PlbscfType.貸借対照表:
            list = [this.createPlbscf帳票データ(plbscfType, v計画Plbscf.bs)];
            break;

          case PlbscfType.損益計算書:
            // 損益合計
            const pl = v計画Plbscf.pls.find((p) => p.部門pk === undefined)!;
            list = [this.createPlbscf帳票データ(plbscfType, pl, true)];
            break;

          default:
            const typeList = v計画Plbscf.types.find((t) => t.type === plbscfType && t.部門pk === undefined)!;
            list = [this.createPlbscf帳票データ(plbscfType, typeList, true, true)];
            break;
        }
        break;
    }
    this.setIndexedXlsxSheetNames(list);
    return list;
  }

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  /**
   * 指定計算コード、年月に紐づく値を取得する
   *
   * @param code NXXX
   * @param ym
   * @returns
   */
  public getValueByCodeAndYm(code: string, ym: number): number {
    return this.getValueByYmAndCodeOr勘定科目Pk(ym, code);
  }

  /**
   * 指定計算コード、v決算期Year、monthlyIndexに紐づく値を取得する
   *
   * @param code NXXX
   * @param v決算期Year
   * @param monthlyIndex
   * @returns
   */
  public getValueByCodeAnd決算期年月(code: string, v決算期Year: number, monthlyIndex?: number, isYear = false): number {
    const map = monthlyIndex === undefined ? this.allCalcResultMap! : this.monthlyCalcResultMap!;
    return this.getValueByCode(map, [], code, v決算期Year, monthlyIndex, undefined, isYear);
  }

  /**
   * 指定勘定科目Pk、年月に紐づく値を取得する
   *
   * @param 勘定科目Pk
   * @param ym
   * @returns
   */
  public getValueBy勘定科目PkAndYm(勘定科目Pk: number, ym: number): number {
    return this.getValueByYmAndCodeOr勘定科目Pk(ym, undefined, 勘定科目Pk);
  }

  /**
   * 指定勘定科目Pk、v決算期Year、monthlyIndexに紐づく値を取得する
   *
   * @param 勘定科目Pk
   * @param v決算期Year
   * @param monthlyIndex
   * @returns
   */
  public getValueBy勘定科目PkAnd決算期年月(勘定科目Pk: number, v決算期Year: number, monthlyIndex?: number): number {
    const foundYv = this.getPlbscfYearValueBy勘定科目PkAnd決算年And部門Pk(勘定科目Pk, v決算期Year);
    const result = AbstractPlbscfService.getOptionValueIfExistOrDBValue(foundYv, monthlyIndex);
    return result === undefined ? 0 : result;
  }

  /**
   * 指定勘定科目Pk、v決算期Year、部門Pkに紐づくPlbscfYearValueを取得
   *
   * @param 勘定科目Pk
   * @param v決算期Year
   * @param monthlyIndex
   * @returns
   */
  public getPlbscfYearValueBy勘定科目PkAnd決算年And部門Pk(
    勘定科目Pk: number,
    v決算期Year: number,
    部門Pk?: number
  ): PlbscfYearValue | undefined {
    return this.calcTypeLists
      .filter((t) => t.部門pk === 部門Pk)
      .flatMap((t) => t.list)
      .filter((listItem) => listItem.勘定科目?.pk === 勘定科目Pk)
      .flatMap((listItem) => listItem.yearValues)
      .find((yv) => yv.year === v決算期Year);
  }

  /**
   * 再計算し、画面表示用データを生成
   *
   * @returns
   */
  public calcAndCreate計画Plbscf(): 計画Plbscf {
    // 計算用科目別計画データを作成
    this.calc科目別計画s = this.部門s.map((d) => ({
      部門pk: d.pk,
      list: this.create科目別計画ListItems(d.pk),
    }));

    // 計算用データを作成
    this.setCalcTypeLists();
    // 損益合計算出／転記
    this.setTotal損益AndTranscribe();
    // 計算結果を設定
    this.setValuesByCalcResult();

    // 計算用補助科目別計画データを作成
    this.calc補助科目別計画List = this.create補助科目別計画ListItems();

    return this.create計画Plbscf();
  }

  /**
   * 画面表示用データを生成
   *
   * @returns
   */
  public create計画Plbscf(changeDisplay = false): 計画Plbscf {
    if (changeDisplay) {
      this.setTotal損益AndTranscribe();
      this.setValuesByCalcResult();
      this.calc補助科目別計画List = this.create補助科目別計画ListItems();
    }

    const 計画書 = AbstractデータService.計画書;
    const is過去データ表示あり = 計画書.c過去データ表示フラグ === E有無フラグ.あり;
    const is0円非表示 = 計画書.c計画0円非表示フラグ === E0円非表示フラグ.非表示;
    const 前期Year = 決算期Utility.get計画0期の決算年() - 1;

    // 計画書データの状態に応じて返却値を絞り込む
    // ヘッダー絞り込み
    const filterHeader = (header: 決算期TableHeader[]): 決算期TableHeader[] => {
      return is過去データ表示あり ? header : header.filter((h) => h.決算年 >= 前期Year || h.isSummary);
    };
    const key = E計画表示モード[計画書.c計画表示モード] as keyof typeof E計画表示モード;
    const plbsHeader = filterHeader(this.plbsHeaders[key]);
    const 科目別Header = filterHeader(this.科目別Headers[key]);

    // 行データ絞り込み
    const allTypes: PlbscfTypeList[] = this.calcTypeLists.map((t) => {
      // c過去データ表示フラグ
      const header = filterHeader(t.header);
      let list = is過去データ表示あり
        ? t.list
        : t.list.map((listItem) => ({
            ...listItem,
            yearValues: listItem.yearValues.filter((yv) => yv.year >= 前期Year),
          }));

      // c計画0円非表示フラグ
      if (is0円非表示) list = this.filterNotZero(list);
      if (t.type === PlbscfType.損益計算書) list = this.filter製造原価科目未使用(list);
      if (t.type === PlbscfType.FCF) list = this.filterFCF種別(list);
      return {
        ...t,
        header,
        list,
      };
    });

    // 貸借・損益
    const 貸借typeList = allTypes.find((t) => t.type === PlbscfType.貸借対照表)!;
    const 損益typeLists = allTypes.filter((t) => t.type === PlbscfType.損益計算書);
    const bs: Bs = {
      ...貸借typeList,
      header: plbsHeader,
      list: this.createPlbs(貸借typeList.list),
    };
    const pls: Pl[] = 損益typeLists.map((t) => ({
      header: plbsHeader,
      部門pk: t.部門pk,
      list: this.createPlbs(t.list, true),
    }));

    // 上記以外
    const types: PlbscfTypeList[] = allTypes.filter(
      (t) => ![PlbscfType.貸借対照表, PlbscfType.損益計算書].includes(t.type)
    );

    // 補助科目別・科目別
    const list: 補助科目別計画ListItem[] = this.filter科目別List(this.calc補助科目別計画List, is0円非表示);
    const 科目別計画s = this.filter科目別計画(is0円非表示);

    return {
      bs,
      pls,
      types,
      補助科目別貸借対照表: {
        header: 科目別Header,
        list,
      },
      科目別損益計算書: {
        header: 科目別Header,
        科目別計画s,
      },
    };
  }

  // ------------------------------------------------------------------
  // override protected methods
  // ------------------------------------------------------------------

  // @override
  protected setTotal損益AndTranscribe(計画TypeLists: PlbscfTypeList[] = []): void {
    this.transcribe科目別ToPls();

    super.setTotal損益AndTranscribe(計画TypeLists);

    this.transcribe当期純利益ToBs();
    this.transcribe繰越利益剰余金ToBs();
  }

  // @override
  protected transcribe(): void {
    super.transcribe();

    this.transcribe期末簿価ToBs();
    this.transcribeToPls();
  }

  // @override
  protected create貸借CalcConfig(): 貸借CalcConfig | undefined {
    const 前期Year = 決算期Utility.get計画0期の決算年() - 1;
    const get前期Value = (code: string): number => {
      return this.分析PlbscfService!.getValueBy分析CalcResultMap([], code, 前期Year);
    };
    return {
      前期Year: 前期Year,
      回転期間s: {
        売上債権: get前期Value('N606'),
        棚卸資産: get前期Value('N607'),
        仕入債務: get前期Value('N608'),
      },
      集計転記科目合計s: {
        売上債権: get前期Value('N602'),
        棚卸資産: get前期Value('N603'),
        仕入債務: get前期Value('N604'),
      },
    };
  }

  // @override
  protected filterYearItems(yearItems: PlbscfYearItem[]): PlbscfYearItem[] {
    const 計画書 = AbstractデータService.計画書;
    const 計画0期Year = 決算期Utility.get計画0期の決算年();
    let filteredItems = yearItems;

    // c過去データ表示フラグ
    if (計画書.c過去データ表示フラグ === E有無フラグ.なし) {
      filteredItems = yearItems.filter((yi) => yi.year >= 計画0期Year - 1);
    }

    const filterWithMonths = (year: number): PlbscfYearItem[] => {
      return filteredItems
        .filter((yi) => yi.year <= year)
        .map((yi) => ({
          ...yi,
          withMonths: yi.year === year ? true : undefined,
          type: yi.year < year ? YearItemType.readonly : yi.type,
        }));
    };
    // c計画表示モード
    switch (計画書.c計画表示モード) {
      case E計画表示モード.月次計画第0期:
        return filterWithMonths(計画0期Year);

      case E計画表示モード.月次計画第1期:
        return filterWithMonths(計画0期Year + 1);

      default:
        return filteredItems.map((yi) => {
          // c第1期月次作成フラグ
          const readonlyYear = 計画書.c第1期月次作成フラグ === E有無フラグ.あり ? 計画0期Year + 1 : 計画0期Year;
          return {
            ...yi,
            type: yi.year <= readonlyYear ? YearItemType.readonly : yi.type,
          };
        });
    }
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 補助科目別貸借対照表の一覧部を作成
   *
   * @returns
   */
  private create補助科目別計画ListItems(): 補助科目別計画ListItem[] {
    const ngPks = this.勘定科目s.filter((k) => !this.canSet補助科目(k)).map((k) => k.pk);
    return this.calcTypeLists
      .find((t) => t.type === PlbscfType.貸借対照表)!
      .list.filter((plbscfListItem) => {
        const pk = plbscfListItem.yearValues[0].item.勘定科目Pk;
        if (pk === undefined) return false;
        return !ngPks.includes(pk);
      })
      .flatMap((plbscfListItem) => {
        const 通常科目 = plbscfListItem.config.label.name;
        const 補助科目s = this.勘定科目s
          .filter((hk) => hk.c勘定科目 === 通常科目 && hk.c補助科目フラグ === E補助科目フラグ.補助)
          .map((hk) => ({
            pk: hk.pk,
            name: hk.c補助科目!,
          }));
        補助科目s.push({
          pk: plbscfListItem.yearValues[0].item.勘定科目Pk!,
          name: '指定なし',
        });
        const 補助科目別計画ListItems: 補助科目別計画ListItem[] = 補助科目s.map((hk, i) => {
          const canEdit = i < 補助科目s.length - 1;
          return {
            通常科目:
              i === 0
                ? {
                    name: 通常科目,
                    itemCount: 補助科目s.length + 1,
                  }
                : undefined,
            補助科目: hk.name,
            canEdit,
            yearItems: canEdit ? this.getYearItems(this.all決算期, hk.pk, true) : plbscfListItem.yearValues,
          };
        });

        補助科目別計画ListItems.push({
          補助科目: '合計',
          canEdit: false,
          yearItems: this.sumPlbscfYearItems(補助科目別計画ListItems),
        });
        return 補助科目別計画ListItems;
      });
  }

  /**
   * 指定計算コードor勘定科目Pk、年月に紐づく値を取得する
   *
   * @param ym
   * @param code
   * @param 勘定科目Pk
   * @returns
   */
  private getValueByYmAndCodeOr勘定科目Pk(ym: number, code?: string, 勘定科目Pk?: number): number {
    const v決算月 = 決算期Utility.getMonth(AbstractデータService.計画書.c計画0期の決算期);
    const year = 決算期Utility.getYear(ym);
    const month = 決算期Utility.getMonth(ym);
    const dayjsYm = dayjs().year(year).month(month);
    let dayjs決算月 = dayjs().year(year).month(v決算月);
    let monthlyIndex = 0;
    const v決算期Year = dayjsYm.isAfter(dayjs決算月) ? year + 1 : year;

    for (let i = 0; i < MONTHS_OF_YEAR; i++) {
      if (dayjs決算月.month() + 1 === month) {
        monthlyIndex = i;
        break;
      }
      dayjs決算月 = dayjs決算月.add(1, 'month');
    }
    return code !== undefined
      ? this.getValueByCodeAnd決算期年月(code, v決算期Year, monthlyIndex)
      : 勘定科目Pk !== undefined
      ? this.getValueBy勘定科目PkAnd決算期年月(勘定科目Pk, v決算期Year, monthlyIndex)
      : 0;
  }

  /**
   * 補助科目別の帳票データを生成
   *
   * @returns
   */
  private create補助科目別帳票データs(): 帳票データ[] {
    const v補助科目別貸借対照表 = 計画PlbscfService.計画Plbscf!.補助科目別貸借対照表;
    // テンプレート行設定
    const templateConfig = {
      header: 0,
      row: {
        body: 1,
        total: 2,
        grey: 3,
      },
    };

    // 以下帳票データ整形
    // 初行設定込みで初期化
    const templateRows: number[] = [templateConfig.header];
    const headerLabels: string[] = [AbstractデータService.getMoneyUnitLabel(), ''];
    const values: unknown[][] = [[...headerLabels, ...v補助科目別貸借対照表.header.map((h) => h.label)]];
    const dynamicConfigs = this.create帳票DynamicConfig(v補助科目別貸借対照表.header, headerLabels.length);
    let isGrey = false;

    // 行でループ
    for (const listItem of v補助科目別貸借対照表.list) {
      let v通常科目 = '';
      if (listItem.通常科目 !== undefined) {
        v通常科目 = listItem.通常科目.name;
        // 通常科目の初行が編集不可ならば、その通常科目のブロックはすべてグレー
        isGrey = !listItem.canEdit;
      }
      const config = isGrey
        ? templateConfig.row.grey
        : listItem.canEdit
        ? templateConfig.row.body
        : templateConfig.row.total;
      templateRows.push(config);

      // 一行追加
      const datas: (string | number)[] = [v通常科目, listItem.補助科目];
      listItem.yearItems.forEach((yi) => {
        datas.push(this.getMoneyUnitValue(yi.optionValue === undefined ? yi.item.value : yi.optionValue));
        if (yi.withMonths && yi.item.monthlyValues !== undefined) {
          yi.item.monthlyValues.forEach((mv) => datas.push(this.getMoneyUnitValue(mv)));
        }
      });
      values.push(datas);
    }
    return [
      {
        templateRows,
        values,
        dynamicConfigs,
      },
    ];
  }

  /**
   * 貸借・損益データを生成する
   *
   * @param list
   * @returns
   */
  private createPlbs(list: PlbscfListItem[], isYConfig = false): Plbscf業績予測ListItem[] {
    const 計画書 = AbstractデータService.計画書;
    const c計画表示モード = 計画書.c計画表示モード;
    const include業績予測 = c計画表示モード !== E計画表示モード.年次計画;

    let filteredList = list.map((listItem) => {
      const config = listItem.config;
      const yearValues = this.filterYearItems(listItem.yearValues);
      let 業績予測: 業績予測 | undefined = undefined;
      if (include業績予測) {
        業績予測 = {};
        if (config.valueType !== ValueType.noValue && config.unitNotYen === undefined) {
          const yvLength = yearValues.length;
          const value1 = AbstractPlbscfService.getYearValue(yearValues[yvLength - 1], isYConfig);
          const value2 = AbstractPlbscfService.getYearValue(yearValues[yvLength - 2], isYConfig);
          業績予測.差異 = value1 - value2;
          業績予測.比率 = this.calcPercentByBigNumber(value1, 100, value2, this.bnRoundingMode.実績計画);
        }
      }
      return {
        ...listItem,
        yearValues,
        業績予測,
      };
    });

    // 0円非表示フラグ
    if (計画書.c計画0円非表示フラグ === E0円非表示フラグ.非表示) {
      filteredList = this.filterNotZero(filteredList);
    }

    return filteredList;
  }

  /**
   * 月次計画ヘッダーを作成する
   *
   * @param is0期
   * @param isPlbs
   * @returns
   */
  private create月次計画Header(is0期 = false, isPlbs = false): 決算期TableHeader[] {
    let year = 決算期Utility.get計画0期の決算年();
    if (!is0期) year += 1;
    const filteredOnlyYearHeader = this.onlyYear決算期.header
      .filter((h) => h.決算年 <= year)
      .map((h) => ({
        ...h,
        isSummary: isPlbs ? [year, year - 1].includes(h.決算年) : h.決算年 === year,
      }));
    const 月次Header = this.all決算期.header.filter((h) => h.決算年 === year && h.month !== undefined);
    const 差異比率Header = isPlbs
      ? 決算期Utility.create差異比率Header(is0期 ? '第0期' : '第1期', is0期 ? '前期' : '第0期')
      : [];
    return [...filteredOnlyYearHeader, ...差異比率Header, ...月次Header];
  }
}
